import type { ReactNode } from "react";
import { Users } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  DropdownMenuLabel,
  DropdownMenuRadioItem,
} from "@lytra/frontend-common/components/v2/ui/dropdown-menu";
import { Icon } from "@lytra/frontend-common/components/v2/ui/icon";
import { SelectItem } from "@lytra/frontend-common/components/v2/ui/select";
import { cn } from "@/lib/utils";
import type { AssigneeOption } from "./ticketHelpers";

/** The rows of a person-or-team picker (#960): each team with a team icon and its
 * members indented below it, then the people in no team. Shared by the ticket header
 * and the ticket list so both show the same hierarchy. */
export function AssigneeSelectItems({
  options,
}: {
  options: readonly AssigneeOption[];
}) {
  return options.map((option) => (
    <SelectItem
      key={option.value}
      value={option.value}
      icon={option.team ? Users : undefined}
      className={cn(option.team && "font-semibold", option.nested && "pl-8")}
    >
      {option.label}
    </SelectItem>
  ));
}

/** Rows that preview their chip colour with a dot (status, priority). Shared by the
 * ticket header and the ticket list filter so both pickers look the same. */
export function DotSelectItems({
  options,
  dotClassFor,
}: {
  options: readonly { value: string; label: string }[];
  dotClassFor: (value: string) => string | undefined;
}) {
  return options.map((option) => (
    <SelectItem key={option.value} value={option.value}>
      <span className="inline-flex items-center gap-2">
        <span
          aria-hidden="true"
          className={cn(
            "h-[7px] w-[7px] shrink-0 rounded-full",
            dotClassFor(option.value),
          )}
        />
        {option.label}
      </span>
    </SelectItem>
  ));
}

/** The same rows as AssigneeSelectItems, as radio items of a menu (the ticket list's
 * owner split button). Wrap them in a DropdownMenuRadioGroup.
 *
 * A team row IS the broad choice (the team and its members); there is no separate
 * row for it. Grouping its members under it, under a "Mitglieder" label, and its
 * narrower "Nur dem Team direkt zugewiesen" sibling after them (not before) is what
 * lets every row read on its own: the team's name sits once, right above everything
 * it covers. When that broad row is the current selection, a tint spans the team row
 * and its members -- exactly who a broad pick of this team covers -- and stops before
 * the narrow row, which that pick does not include. */
export function AssigneeMenuItems({
  options,
  selected,
}: {
  options: readonly AssigneeOption[];
  selected?: string;
}) {
  const { t } = useTranslation();
  const rows: ReactNode[] = [];
  let i = 0;
  while (i < options.length) {
    const option = options[i];
    if (!option.team) {
      rows.push(
        <DropdownMenuRadioItem key={option.value} value={option.value}>
          {option.label}
        </DropdownMenuRadioItem>,
      );
      i++;
      continue;
    }
    const members: AssigneeOption[] = [];
    let j = i + 1;
    while (j < options.length && options[j].nested && !options[j].teamOnly) {
      members.push(options[j]);
      j++;
    }
    const teamOnly = options[j]?.teamOnly ? options[j] : undefined;
    if (teamOnly) j++;
    const teamSelected = selected === option.value;
    rows.push(
      <div
        key={option.value}
        className={cn(teamSelected && "rounded-md bg-primary-subtle")}
      >
        <DropdownMenuRadioItem value={option.value} className="font-semibold">
          <Icon icon={Users} />
          {option.label}
        </DropdownMenuRadioItem>
        {members.length > 0 && (
          <DropdownMenuLabel inset className="py-0.5">
            {t("tickets.filters.teamMembers")}
          </DropdownMenuLabel>
        )}
        {members.map((member) => (
          <DropdownMenuRadioItem
            key={member.value}
            value={member.value}
            className="pl-8"
          >
            {member.label}
          </DropdownMenuRadioItem>
        ))}
        {teamOnly && (
          <DropdownMenuRadioItem
            value={teamOnly.value}
            className="pl-8 text-caption text-ink-muted"
          >
            {teamOnly.label}
          </DropdownMenuRadioItem>
        )}
      </div>,
    );
    i = j;
  }
  return <>{rows}</>;
}
